import React, { useState, useMemo, useEffect } from 'react';
import { View, ScrollView } from 'react-native';
import { styles } from './AddFreight.styles';
import Typography from '../../../../components/Text/Text';
import TourWrapper from 'src/components/TourWrapper/TourWrapper';
import { useTourAutoStart } from 'src/features/tour/hooks/useTourAutoStart';
import { getFreightTour } from 'src/features/tour/tourSteps';
import Button from '../../../../components/Button/Button';
import { useLinkTo } from '@react-navigation/native';
import CommoditiesSlider from '../../components/CommoditiesSlider';
import CommoditiesModal from 'src/components/CheckListModal/ChecklistModal';
import Toast from 'src/components/Toast/Toast';
import { EnhancedSelection } from '@typeRoots/common/freight';
import { CheckListModalList, Selection } from '@typeRoots/common/checkListModal';
import { URLS } from 'src/constants/urls';
import { setActiveTab, setFilledTabsIndex } from 'src/slices/UiSlice';
import { useDispatch, useSelector } from 'react-redux';
import {
  useGetCommoditiesListQuery,
  useGetCompanyCommoditiesQuery,
  useUpdateCompanyCommoditiesMutation,
} from 'src/features/freight/api/freightApi';

import { calculateSum, prepareEnhancedSelection } from './AddFreight.utils';
import { TABS_INDEX } from 'src/constants/tabs';
import { RootState } from 'src/store/store';
import { toggleToastState } from 'src/helpers/toastHelper';
import { trackGTMEvent } from 'src/helpers/gtm/gtmEvents'; // Import GTM helper method

const AddFreight: React.FC = () => {
  const linkTo = useLinkTo();
  const dispatch = useDispatch();

  const currentCompany = useSelector((state: RootState) => state.company.company);
  const accountType = useSelector((state: RootState) => state.UI.accountType);

  const [enhancedSelection, setEnhancedSelection] = useState<EnhancedSelection[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  const [sum, setSum] = useState(0);
  const [hasZeroPercentage, setHasZeroPercentage] = useState(false);

  // api calls
  const { data: freightList } = useGetCommoditiesListQuery();
  const { data: companyFreightList, isSuccess: isCompanyFreightListSuccess } =
    useGetCompanyCommoditiesQuery(currentCompany?.id || 0);
  const [updateCompanyCommodities] = useUpdateCompanyCommoditiesMutation();

  const [freightErrorToast, setFreightErrorToast] = useState(false);

  let selectedCommodities = useMemo(
    () =>
      enhancedSelection.map(({ type, subtype }) => ({
        type,
        subtype,
      })),
    [enhancedSelection],
  );

  // Track Commodities Page Landed event
  useEffect(() => {
    trackGTMEvent(
      'commodities-page-landed',
      {
        userEvent: 'User landed on the Commodities Screen',
      },
      accountType,
    );
  }, []);

  useEffect(() => {
    if (isCompanyFreightListSuccess) {
      selectedCommodities = companyFreightList.map(({ type, subtype }) => ({
        type: type as string,
        subtype: subtype,
      }));

      let currentSelected = companyFreightList.map(
        ({ type, subtype, percentageOfHauls, active }) => ({
          type: type as string,
          subtype: subtype,
          percentageOfHauls: percentageOfHauls as number,
          active: active as boolean,
        }),
      );

      const total = currentSelected.reduce((sum, item) => sum + item.percentageOfHauls, 0);

      // Update the sum state
      setSum(total);
      setEnhancedSelection(currentSelected);
    }
  }, [companyFreightList, isCompanyFreightListSuccess]);

  // Auto-start tour on first visit (after data is loaded)
  useTourAutoStart({
    tourKey: 'freightStart',
    driverSteps: getFreightTour(),
    enabled: !!freightList && !!currentCompany,
  });

  const handleSliderChange = (index: number, newValue: number) => {
    setEnhancedSelection((prev) => {
      const updatedSelection = prev.map((item, idx) =>
        idx === index ? { ...item, percentageOfHauls: newValue } : item,
      );

      // Calculate the sum of percentages immediately
      const total = updatedSelection.reduce((sum, item) => sum + item.percentageOfHauls, 0);

      if (total === 100 && sum !== 100) {
        trackGTMEvent(
          'commodities-percentage-set',
          {
            userEvent: 'User adjusted sliders to set total commodities percentage to 100%.',
          },
          accountType,
        );
      }

      // Update the sum state
      setSum(total);

      return updatedSelection;
    });
  };

  const handleModalSave = (newSelection: Selection[]) => {
    setEnhancedSelection((prev) => {
      const updatedEnhancedSelection = prepareEnhancedSelection(newSelection, prev);

      // Calculate the sum of percentages immediately
      const total = calculateSum(enhancedSelection);
      // Update the sum state
      setSum(total);
      return updatedEnhancedSelection;
    });

    // Track GTM Event: Commodities Chosen
    trackGTMEvent(
      'commodities-chosen',
      {
        userEvent: 'User selected commodities and pressed Confirm',
      },
      accountType,
    );

    setIsVisible(false);
  };

  const handleDelete = (index: number) => {
    setEnhancedSelection((prev) => {
      const updatedSelection = prev.filter((_, idx) => idx !== index);

      // Recalculate the sum after deletion
      const total = updatedSelection.reduce((sum, item) => sum + item.percentageOfHauls, 0);
      setSum(total);

      return updatedSelection;
    });

    // Track GTM Event: Commodities Deleted
    trackGTMEvent(
      'commodity-delete-button-pressed',
      {
        userEvent: 'User pressed delete button on a commodity',
      },
      accountType,
    );
  };

  useEffect(() => {
    if (hasZeroPercentage) {
      const timeout = setTimeout(() => {
        setHasZeroPercentage(false);
      }, 3000);
      return () => clearTimeout(timeout); // Cleanup timeout
    }
  }, [hasZeroPercentage]);

  // Track Add Commodities Clicked event
  const openModal = () => {
    trackGTMEvent(
      'add-commodities-clicked',
      {
        userEvent: 'User clicked Add Commodities button.',
      },
      accountType,
    );
    setIsVisible(true);
  };

  // Track Add Commodities Canceled event
  const handleCancelModal = () => {
    trackGTMEvent(
      'add-commodities-modal-closed',
      {
        userEvent: 'Add commodities modal closed',
      },
      accountType,
    );
    setIsVisible(false);
  };

  const handleNext = async () => {
    const percentage = enhancedSelection.some(({ percentageOfHauls }) => percentageOfHauls === 0);
    // show warning toast
    if (percentage) {
      setHasZeroPercentage(percentage);
      return;
    }
    try {
      const data = enhancedSelection.map(({ subtype, active, percentageOfHauls }) => ({
        subtype: subtype,
        active,
        percentageOfHauls,
      }));

      await updateCompanyCommodities({
        id: currentCompany?.id || 0,
        data,
      }).unwrap(); // Call the mutation and wait for the result

      // Track Commodities Details Given event
      trackGTMEvent(
        'commodities-details-given',
        {
          userEvent:
            'User clicked Next after selecting commodities and setting their percentage to 100%.',
        },
        accountType,
      );

      dispatch(setActiveTab({ activeTab: TABS_INDEX.FINAL_DETAILS }));
      //I will modify this when doing real time data for policy and lob pages
      dispatch(setFilledTabsIndex({ filledTabsIndex: TABS_INDEX.FINAL_DETAILS }));
      //I think this will be linked to LOB pages
      linkTo(URLS.APPLICATION.INSURANCE);
      //dispatch(setCompanyCommodities(data)); //this is not needed as api already does this
    } catch (err) {
      setFreightErrorToast(true);
      toggleToastState(setFreightErrorToast);
      console.error('An error occurred while saving the driver:', err);
    }
  };

  const handleBack = () => {
    trackGTMEvent(
      'commodities-page-left',
      {
        userEvent: 'User clicked Back and navigated to the Driver Summary screen',
      },
      accountType,
    );

    linkTo(URLS.DRIVER.BYPASS);
    dispatch(setActiveTab({ activeTab: TABS_INDEX.DRIVER }));
  };
  let frightModalList = {};
  if (freightList) {
    frightModalList = Object.fromEntries(Object.entries(freightList as CheckListModalList).sort());
  }

  return (
    <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.container}>
      <CommoditiesModal
        isVisible={isVisible}
        initialSelections={selectedCommodities}
        handleSelectionChange={handleModalSave}
        onClose={handleCancelModal}
        title="Commodities"
        list={frightModalList}
        parentTestID="addfreight"
        hasSearch={true}
      />
      <View style={styles.topContainer}>
        <TourWrapper tourId="freight-title">
          <Typography variant="h2">How often do you haul each of these?</Typography>
        </TourWrapper>
        {selectedCommodities.length > 0 && (
          <TourWrapper tourId="freight-toast">
            <Toast
              text={
                sum === 100
                  ? 'Good to go! Your commodities total 100%'
                  : 'Your commodities total needs to equal 100%.'
              }
              type={sum === 100 ? 'success' : 'idea'}
              animate={false}
            />
          </TourWrapper>
        )}

        <TourWrapper tourId="commodities-list">
          {enhancedSelection.map((commodity, index) => (
            <CommoditiesSlider
              testID={`add-freight-commodities-${commodity.subtype.value}`}
              key={commodity.subtype.value}
              title={commodity.subtype.name}
              value={commodity.percentageOfHauls}
              onValueChange={(value) => handleSliderChange(index, value)}
              onDelete={() => handleDelete(index)}
              index={index}
            />
          ))}
        </TourWrapper>
        <TourWrapper tourId="add-commodities-btn">
          <Button
            title="Add Commodities"
            type="tertiary"
            onPress={openModal}
            testID="add-freight-btn"
          />
        </TourWrapper>
      </View>
      <View>
        {selectedCommodities.length > 0 && hasZeroPercentage && (
          <Toast
            text={'A commodity percentage should not be 0%. Please remove commodity'}
            type={'error'}
            animate={true}
          />
        )}
        <View>
          {freightErrorToast && (
            <Toast
              text="An error occurred while saving commodities. Please try again."
              type="error"
              animate={true}
            />
          )}

          <View style={styles.buttonContainer}>
            <View style={styles.buttonWrapper1}>
              <Button
                type="tertiary"
                title="Back"
                onPress={handleBack}
                testID={'add-freight-back-btn'}
              />
            </View>
            <View style={styles.buttonWrapper2}>
              <TourWrapper tourId="freight-next-btn">
                <Button
                  type="primary"
                  colorVariant={sum === 100 ? 'default' : 'disabled'}
                  title="Next"
                  onPress={handleNext}
                  testID={'add-freight-next-btn'}
                />
              </TourWrapper>
            </View>
          </View>
        </View>
      </View>
    </ScrollView>
  );
};

export default AddFreight;
